import { useEffect, useRef, useState, type ReactElement } from 'react';
import { api } from '../shared/api/endpoints';
import { useMessages } from '../shared/i18n/locale';
import { codeLabel } from '../shared/lib/status';
import { formatTime } from '../shared/lib/format';
import { useAsync } from '../shared/lib/use-async';
import {
  Card,
  ConfirmDialog,
  EmptyState,
  primaryButtonStyle,
  secondaryButtonStyle,
} from '../shared/ui/primitives';

/**
 * Назначение исполнителя — экран назначения кабинета (разд. 6, FR-533, FR-572).
 *
 * Карточки сравнения, а не выпадающий список: решение принимается по нескольким
 * величинам сразу — прирост времени задания, прогноз доставки против дедлайна,
 * загрузка и подача.
 *
 * Блок «Исключены» важнее, чем кажется: когда кандидатов нет вовсе, диспетчеру нужно
 * понять, чего именно не хватает — тары, закрепления или времени, — а не увидеть
 * пустой экран.
 */
export function AssignmentPanel({
  orderId,
  onClose,
  onAssigned,
}: {
  readonly orderId: string;
  readonly onClose: () => void;
  readonly onAssigned: () => void;
}): ReactElement {
  const messages = useMessages();
  const candidates = useAsync(() => api.candidates(orderId), [orderId]);
  const [forcing, setForcing] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const assign = async (driverId: string, force: boolean): Promise<void> => {
    try {
      await api.assign(orderId, {
        driver_id: driverId,
        force,
        ...(force ? { force_reason: reason } : {}),
      });

      onAssigned();
    } catch (cause) {
      setError(String(cause));
    }
  };

  const list = candidates.data?.candidates ?? [];
  const excluded = candidates.data?.excluded ?? [];

  // Панель открывается в конце длинной карточки: без прокрутки к ней нажатие
  // «Назначить» выглядит так, будто ничего не произошло.
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    root.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [orderId]);

  return (
    <div ref={root} style={{ display: 'grid', gap: 'var(--sp-3)' }}>
      <h2>{messages.cabinet.assignment.title}</h2>

      {list.length === 0 ? (
        <EmptyState text={messages.cabinet.assignment.empty} />
      ) : (
        list.map((candidate, index) => (
          <Card key={candidate.driver_id} tone={index === 0 ? 'ok' : undefined}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--sp-3)' }}>
              <strong>{candidate.driver_name}</strong>
              {index === 0 ? (
                <span style={{ color: 'var(--ok)' }}>
                  {messages.cabinet.assignment.recommended}
                </span>
              ) : null}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-2)' }}>
              <span>
                {`${messages.cabinet.assignment.load}: `}
                <span className="num">{candidate.current_load ?? 0}</span>
              </span>
              <span>
                {`${messages.cabinet.assignment.increase}: `}
                <span className="num">
                  {`${candidate.insertion_cost_minutes ?? 0} ${messages.common.minutesShort}`}
                </span>
              </span>
              <span>
                {`${messages.cabinet.assignment.willDeliver} `}
                <span className="num">{formatTime(candidate.forecast_delivery_at)}</span>
              </span>
              <span>
                {`${messages.cabinet.assignment.deadHead}: `}
                <span className="num">{candidate.dead_head_distance_m ?? '—'}</span>
              </span>
            </div>

            <div style={{ display: 'flex', gap: 'var(--sp-2)', justifyContent: 'flex-end' }}>
              {candidate.deadline_ok === false ? (
                // Принудительное назначение не выглядит основным действием и требует
                // причины: решение вопреки правилу разбирают по имени (FR-520).
                <button
                  type="button"
                  style={secondaryButtonStyle}
                  onClick={() => setForcing(candidate.driver_id ?? '')}
                >
                  {messages.cabinet.assignment.force}
                </button>
              ) : (
                <button
                  type="button"
                  style={primaryButtonStyle}
                  onClick={() => void assign(candidate.driver_id ?? '', false)}
                >
                  {messages.cabinet.assignment.assign}
                </button>
              )}
            </div>
          </Card>
        ))
      )}

      {excluded.length === 0 ? null : (
        <details>
          <summary style={{ cursor: 'pointer', color: 'var(--text-muted)' }}>
            {`${messages.cabinet.assignment.excluded} ${excluded.length}`}
          </summary>
          <div style={{ display: 'grid', gap: 'var(--sp-1)', paddingTop: 'var(--sp-2)' }}>
            {excluded.map((item) => (
              <div key={item.driver_id} style={{ display: 'flex', gap: 'var(--sp-3)' }}>
                <span>{item.driver_name ?? messages.cabinet.assignment.unknownDriver}</span>
                <span style={{ color: 'var(--text-muted)' }}>
                  {codeLabel(messages.cabinet.assignment.exclusions, item.reason_code)}
                </span>
              </div>
            ))}
          </div>
        </details>
      )}

      <button type="button" style={secondaryButtonStyle} onClick={onClose}>
        {messages.cabinet.common.close}
      </button>

      {error === null ? null : <div style={{ color: 'var(--danger)' }}>{error}</div>}

      {forcing === null ? null : (
        <ConfirmDialog
          title={messages.cabinet.assignment.force}
          description={messages.cabinet.assignment.forceReason}
          requireReason
          reason={reason}
          onReason={setReason}
          onCancel={() => setForcing(null)}
          onConfirm={() => {
            const driverId = forcing;
            setForcing(null);
            void assign(driverId, true);
          }}
        />
      )}
    </div>
  );
}
